import { useState, useEffect, useCallback } from 'react';
import ContentLayout from '../../layout/ContentLayout';
import styles from '../../styles/pointsAndGamification.module.scss';
import GenericTable from '../../components/tables/GenericTable';
import Pagination from '../../components/layout/Pagination';
import ListItem from './listItem';
import Search from 'antd/es/transfer/search';
import {
  fetchLoyalty,
  fetchUserHistory,
  fetchUserVouchers,
  adminAwardLoyaltyPoints,
  adminDeductLoyaltyPoints,
} from '../../api/loyalty-api';
import dayjs from 'dayjs';
import useResize from '../../hooks/useResize';
import Spinner from '../../components/elements/Spinner/Spinner';
import { toast } from 'react-toastify';
import AwardLoyaltyPointsModal from '../../components/modals/AwardLoyaltyPointsModal';
import DeductLoyaltyPointsModal from '../../components/modals/DeductLoyaltyPointsModal';

const PointsAndGamification = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [historyId, setHistoryId] = useState(null);
  const [individualUserHistory, setIndividualUserHistory] = useState(null);
  const [individualUserVouchers, setIndividualUserVouchers] = useState(null);
  const [loyalty, setLoyalty] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const windowWidth = useResize();
  const [loading, setLoading] = useState(true);

  const [isAwardPointsModalOpen, setIsAwardPointsModalOpen] = useState(false);
  const [awardPointsAccount, setAwardPointsAccount] = useState(null);
  const [loadingAwardPoints, setLoadingAwardPoints] = useState(false);
  const [isDeductPointsModalOpen, setIsDeductPointsModalOpen] = useState(false);
  const [deductPointsAccount, setDeductPointsAccount] = useState(null);
  const [loadingDeductPoints, setLoadingDeductPoints] = useState(false);

  const onSearchLoyalty = (value) => {
    setSearch(value);
  };

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAwardPoints = async (points) => {
    setLoadingAwardPoints(true);
    const userId = awardPointsAccount.user?._id;
    const prevPoints = awardPointsAccount.points ?? 0;

    setLoyalty((prev) =>
      prev.map((acc) =>
        acc.user?._id === userId ? { ...acc, points: prevPoints + points } : acc
      )
    );
    setIsAwardPointsModalOpen(false);
    setAwardPointsAccount(null);

    const { success, message } = await adminAwardLoyaltyPoints(userId, points);

    if (!success) {
      setLoyalty((prev) =>
        prev.map((acc) => (acc.user?._id === userId ? { ...acc, points: prevPoints } : acc))
      );
      toast.error(message || 'Greška pri dodavanju poena');
    } else {
      toast.success('Poeni uspešno dodati');
    }

    setLoadingAwardPoints(false);
  };

  const handleDeductPoints = async (points, deductionMessage) => {
    setLoadingDeductPoints(true);
    const userId = deductPointsAccount.user?._id;
    const prevPoints = deductPointsAccount.points ?? 0;

    setLoyalty((prev) =>
      prev.map((acc) =>
        acc.user?._id === userId
          ? { ...acc, points: Math.max(0, prevPoints - points) }
          : acc
      )
    );
    setIsDeductPointsModalOpen(false);
    setDeductPointsAccount(null);

    const { success, message } = await adminDeductLoyaltyPoints(
      userId,
      points,
      deductionMessage
    );

    if (!success) {
      setLoyalty((prev) =>
        prev.map((acc) => (acc.user?._id === userId ? { ...acc, points: prevPoints } : acc))
      );
      toast.error(message || 'Greška pri oduzimanju poena');
    } else {
      toast.success('Poeni uspešno oduzeti');
    }

    setLoadingDeductPoints(false);
  };

  const formatVoucherStatus = (status) => {
    switch (status) {
      case 'used':
        return 'Iskorišćen';
      case 'expired':
        return 'Istekao';
      case 'unused':
        return 'Na raspolaganju';
      default:
        return status;
    }
  };
  const columns = [
    {
      key: 'firstLastName',
      label: 'Korisnik',
      render: (val, row) => {
        const joinedNames = `${row?.userInfo?.firstName} ${row?.userInfo?.lastName}`;
        const maxLength = windowWidth < 768 ? 25 : 40;
        const displayName =
          joinedNames.length > maxLength
            ? joinedNames.slice(0, maxLength) + '...'
            : joinedNames;
       return  (<div
          style={{ cursor: 'pointer' }}
          onClick={() => setHistoryId(row?.user?._id)}
        >
          {displayName}
        </div>)
      },
    },
    { key: 'points', label: 'Broj poena' },
  ];

  const getTableData = useCallback(async () => {
    const { data, success, message } = await fetchLoyalty({
      page: currentPage,
      limit: 10,
      // filter: appliedFilters,
      search: debouncedSearch,
    });
    if (success) {
      console.log('Loyalty data fetched:', data);
      setLoyalty([...data?.items]);
      setTotalPages(data?.totalPages);
    } else {
      console.log(message || 'Error while fetching the users.');
    }
    setLoading(false);
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      // Reset to page 1 when search changes
      if (search !== debouncedSearch) {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  const getUserHistory = useCallback(async () => {
    const { data, success, message } = await fetchUserHistory({
      page: 1,
      limit: 10,
      userId: historyId,
      // filter: appliedFilters,
      // search: debouncedSearch,
    });
    if (success) {
      // console.log('Loyalty data fetched:', data);
      setIndividualUserHistory(data);
    } else {
      console.log(message || 'Error while fetching the users.');
    }
  }, [historyId]);

  const getUserVouchers = useCallback(async () => {
    const { data, success, message } = await fetchUserVouchers({
      page: 1,
      limit: 10,
      userId: historyId,
      // filter: appliedFilters,
      // search: debouncedSearch,
    });
    if (success) {
      // console.log('Loyalty data fetched:', data);
      setIndividualUserVouchers(data);
    } else {
      console.log(message || 'Error while fetching the users.');
    }
  }, [historyId]);

  useEffect(() => {
    if (historyId) {
      getUserHistory();
      getUserVouchers();
    }
  }, [getUserHistory, getUserVouchers, historyId]);
  console.log('history id', historyId);
  console.log('individualUserHistory', individualUserHistory);
  console.log('individualUserVouchers', individualUserVouchers);

  return (
    <>
      <ContentLayout pt={true}>
        <div className={styles.pointsAndGamification}>
          <div className={styles.pointsAndGamification__header}>
            Poeni i gamifikacija
          </div>
          <div className={styles.pointsAndGamification__search}>
            <Search
              placeholder={'Pretraži'}
              className="search-input"
              allowClear
              enterButton
              // onSearch={(value) => onSearch(value)}
              onChange={(e) => onSearchLoyalty(e.target.value)}
              width="auto"
            />
          </div>
          <div className={styles.points}>
            <div className={styles.points__grid}>
              <div className={`${styles.points__grid__table} points_table`}>
                {loading ? (
                  <Spinner size="lg" />
                ) : (
                  <>
                    <GenericTable
                      data={loyalty}
                      columns={columns}
                      mobNoScroll={true}
                      actions={[
                        {
                          key: 'award-points',
                          label: 'Dodaj poene',
                          icon: <img src="/assets/icons/edit-icon.svg" alt="Award points" />,
                          onClick: (acc) => {
                            setAwardPointsAccount(acc);
                            setIsAwardPointsModalOpen(true);
                          },
                          visible: (acc) => !!acc.user?._id,
                        },
                        {
                          key: 'deduct-points',
                          label: 'Oduzmi poene',
                          icon: <img src="/assets/icons/edit-icon.svg" alt="Deduct points" />,
                          onClick: (acc) => {
                            setDeductPointsAccount(acc);
                            setIsDeductPointsModalOpen(true);
                          },
                          visible: (acc) => !!acc.user?._id,
                        },
                      ]}
                    />
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={onPageChange}
                    />
                  </>
                )}
              </div>

              {individualUserHistory && (
                <div>
                  {individualUserHistory && (
                    <p /*className={styles.points__activity__header}*/>
                      {/*Istorija aktivnosti korisnika*/}
                    </p>
                  )}
                  <div className={styles.points__activity}>
                    {individualUserHistory && (
                      <div className={styles.points__activity__title}>
                        <span>
                          {individualUserHistory?.user?.firstName}{' '}
                          {individualUserHistory?.user?.lastName}
                        </span>
                        &nbsp;|&nbsp;Trenutni broj poena:
                        <span>&nbsp;{individualUserHistory?.points}</span>
                      </div>
                    )}
                    <div className={styles.points__activity__body}>
                      {individualUserHistory && (
                        <div className={styles.points__activity__body__top}>
                          <p style={{ fontWeight: 700 }}>Dnevna aktivnost</p>
                          <div
                            className={
                              styles.points__activity__body__top__inner
                            }
                          >
                            {individualUserHistory?.transactions?.map(
                              (item) => {
                                console.log('item', item);
                                const dateCreated = dayjs(item.date).format(
                                  'DD.MM.YYYY.'
                                );
                                const accumulatedPoints = item?.items?.reduce(
                                  (acc, curr) => acc + curr.pointsChange,
                                  0
                                );
                                const activities = item?.items;
                                return (
                                  <ListItem
                                    date={dateCreated}
                                    points={accumulatedPoints}
                                    activities={activities}
                                  />
                                );
                              }
                            )}
                          </div>
                        </div>
                      )}
                      {individualUserVouchers &&
                        individualUserVouchers.length > 0 && (
                          <div
                            className={styles.points__activity__body__bottom}
                          >
                            <p style={{ fontWeight: 700 }}>Nagrade i vaučeri</p>
                            <div
                              className={
                                styles.points__activity__body__bottom__inner
                              }
                            >
                              {individualUserVouchers?.map((voucher) => (
                                <div
                                  className={
                                    styles.points__activity__body__bottom__inner__vouchers
                                  }
                                >
                                  <div style={{ fontWeight: 700 }}>
                                    {voucher?.label}
                                  </div>
                                  <div
                                    style={{
                                      fontWeight: 400,
                                      textAlign: 'right',
                                    }}
                                  >
                                    {formatVoucherStatus(voucher?.status)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ContentLayout>
      <AwardLoyaltyPointsModal
        onConfirm={handleAwardPoints}
        onClose={() => {
          setIsAwardPointsModalOpen(false);
          setAwardPointsAccount(null);
        }}
        loading={loadingAwardPoints}
        userName={
          awardPointsAccount
            ? `${awardPointsAccount.userInfo?.firstName ?? ''} ${awardPointsAccount.userInfo?.lastName ?? ''}`
            : ''
        }
        isOpen={isAwardPointsModalOpen}
      />
      <DeductLoyaltyPointsModal
        onConfirm={handleDeductPoints}
        onClose={() => {
          setIsDeductPointsModalOpen(false);
          setDeductPointsAccount(null);
        }}
        loading={loadingDeductPoints}
        userName={
          deductPointsAccount
            ? `${deductPointsAccount.userInfo?.firstName ?? ''} ${deductPointsAccount.userInfo?.lastName ?? ''}`
            : ''
        }
        isOpen={isDeductPointsModalOpen}
      />
    </>
  );
};
export default PointsAndGamification;
